import {EMAIL_MAKER_COMMANDS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import type {Component, ComponentModelDefinition, ComponentView, ComponentViewDefinition} from 'grapesjs';
import {ResponsiveType} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {matchByTagAndAttr} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';

export function registerTableTypes({
  editor,
  defaultToolbarOptions,
  unstylableEmailStyles,
  isDroppableInRow,
  getDraggableForColumn,
  initRemovalOfUnsupportedCssProperties,
}: ComponentRegistrationContext): void {
  const cmpt = editor.Components;
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Table',
        toolbar: defaultToolbarOptions,
        removable: true,
        selectable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_table'),
        badgable: true,
        draggable: [`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER}"]`],
        droppable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        [EMAIL_MAKER_PROPS.HIERARCHY_ROOT]: true,
        [EMAIL_MAKER_PROPS.HIERARCHY_SELECTION]: true,
        propagate: [EMAIL_MAKER_PROPS.HIERARCHY_SELECTION],
        attributes: {
          'data-is-pmw-table': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE,
          cellspacing: 1,
        },
        components: [
          {
            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
            components: [
              {
                tagName: 'tr',
                type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
                components: [{type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}, {type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}],
              },
              {
                tagName: 'tr',
                type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
                components: [{type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}, {type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}],
              },
            ],
          },
        ],
        style: {
          padding: '6px',
          margin: '0px',
          width: '100%',
          'vertical-align': 'middle',
          height: 'auto',
          'font-family': 'Helvetica, sans-serif',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          'border-color': '#000000',
          'border-radius': '0',
          opacity: '100%',
          'pmw-rows': 2,
          'pmw-columns': 2,
          'pmw-cell-border-width': '0px',
          'pmw-cell-border-color': '#242424',
          'pmw-cell-border-radius': '0px',
          'pmw-cell-background-color': 'transparent',
          'pmw-cell-spacing': 1,
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        this.components().forEach((cell: Component) => {
          editor.runCommand(EMAIL_MAKER_COMMANDS.APPLY_GLOBAL_CELL_STYLES, {parent: this, cell});
        });

        editor.runCommand(EMAIL_MAKER_COMMANDS.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER, {component: this, stylesToCopy: ['background-color']});
      },
    },
    view: {
      onRender(this: ComponentViewDefinition & ComponentView, {model}: {model: Component}) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.HANDLE_TABLE_PADDING, {
          component: model,
        });
      },
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-table', EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Section',
        toolbar: defaultToolbarOptions,
        removable: true,
        selectable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_section'),
        badgable: true,
        draggable: [`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER}"]`],
        droppable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        [EMAIL_MAKER_PROPS.COLUMNS_PRESET]: 1,
        [EMAIL_MAKER_PROPS.APPLY_INITIAL_PRESET]: false,
        [EMAIL_MAKER_PROPS.HIERARCHY_ROOT]: true,
        [EMAIL_MAKER_PROPS.HIERARCHY_SELECTION]: true,
        [EMAIL_MAKER_PROPS.IS_RESPONSIVE_MOBILE]: true,
        [EMAIL_MAKER_PROPS.IS_RESPONSIVE_TABLET]: false,
        propagate: [EMAIL_MAKER_PROPS.HIERARCHY_SELECTION],
        attributes: {
          'data-is-pmw-section': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION,
          cellspacing: '0',
          cellpadding: '15',
          class: 'pmw-responsive-table',
        },
        components: [
          {
            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
            components: [
              {
                tagName: 'tr',
                type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
                components: [
                  {
                    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
                    hoverable: true,
                    selectable: true,
                    highlightable: true,
                  },
                ],
              },
            ],
          },
        ],
        style: {
          padding: '15px',
          margin: '0px',
          width: '100%',
          'vertical-align': 'middle',
          height: 'auto',
          'font-family': 'Helvetica, sans-serif',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-radius': '0',
          opacity: '100%',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        if (typeof this.get(EMAIL_MAKER_PROPS.IS_RESPONSIVE_TABLET) === 'undefined' || this.get(EMAIL_MAKER_PROPS.IS_RESPONSIVE_TABLET) === true) {
          editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_SECTION_RESPONSIVE, {
            component: this,
            responsive: true,
            type: ResponsiveType.TABLET,
          });
        }

        this.listenTo(this, 'change:style', () => {
          editor.runCommand(EMAIL_MAKER_COMMANDS.SYNC_TABLE_TBODY_COLORS, {
            component: this,
          });
        });

        editor.runCommand(EMAIL_MAKER_COMMANDS.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER, {component: this, stylesToCopy: ['background-color', 'border-radius']});
      },
    },
    view: {
      onRender(this: ComponentViewDefinition & ComponentView, {model}: {model: Component}) {
        const presetID = Number(model.get(EMAIL_MAKER_PROPS.COLUMNS_PRESET));
        const applyPreset = Boolean(model.get(EMAIL_MAKER_PROPS.APPLY_INITIAL_PRESET));

        if (applyPreset && presetID) {
          editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_COLUMNS_PRESET, {
            component: model,
            presetID,
          });
          model.set(EMAIL_MAKER_PROPS.APPLY_INITIAL_PRESET, false);
        }

        editor.runCommand(EMAIL_MAKER_COMMANDS.HANDLE_TABLE_PADDING, {
          component: model,
        });

        editor.runCommand(EMAIL_MAKER_COMMANDS.SYNC_TABLE_TBODY_COLORS, {
          component: model,
        });
      },
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-section', EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TBODY,
    model: {
      defaults: {
        tagName: 'tbody',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        badgable: false,
        draggable: false,
        hoverable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-tbody': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
        },
      },
      // Outside `defaults` so GrapesJS never applies it; `opacty` is a saved-template typo — do not fix.
      style: {
        'background-color': 'transparent',
        height: '100%',
        width: '100%',
        background: 'none',
        margin: '0',
        opacty: '100%',
        border: 'none',
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'TBODY') {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.ROW,
    model: {
      defaults: {
        tagName: 'tr',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        badgable: false,
        droppable: isDroppableInRow,
        draggable: false,
        hoverable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-row': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: matchByTagAndAttr('TR', 'data-is-pmw-row', EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: 'Cell',
        toolbar: defaultToolbarOptions,
        removable: false,
        name: window.i18next.t('pmwjs_column'),
        badgable: true,
        draggable: getDraggableForColumn(),
        droppable: true,
        copyable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-cell': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
          class: 'pmw-responsive-cell',
        },
        style: {
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          'background-position': 'center',
          'background-color': 'transparent',
          margin: '0px',
          'min-width': '10px',
          padding: '30px',
          width: 'auto',
        },
        components: [
          {
            type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
          },
        ],
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        const isTableParent = this.parent()?.parent()?.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE);
        const name = isTableParent ? window.i18next.t('pmwjs_cell') : window.i18next.t('pmwjs_column');
        this.set('name', name);
      },
    },
    view: {
      onRender(this: ComponentViewDefinition & ComponentView, {model}: {model: Component}) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.HANDLE_TABLE_PADDING, {
          component: model,
        });
      },
    },
    isComponent: matchByTagAndAttr('TD', 'data-is-pmw-cell', EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_STATIC_CELL, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: true,
        selectable: true,
        highlightable: false,
        badgable: false,
        draggable: true,
        hoverable: true,
        stylable: true,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-static-cell': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.table.PMW_STATIC_CELL,
        },
        style: {
          padding: '0px',
          margin: '0px',
          'min-width': '10px',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'TD' && !el.getAttribute('data-is-pmw-cell')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_STATIC_CELL,
        };
      }

      return false;
    },
  });
}
